import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { ProductAccessService } from 'src/app/services/product-access/product-access.service';

export const productAccessGuard: CanActivateFn = async () => {
  const productAccess = inject(ProductAccessService);
  const organization = inject(OrganizationService);
  const firebase = inject(FirebaseService);
  const router = inject(Router);

  // Wait for organization to be loaded — product access evaluation needs the
  // org id and the current member doc.
  if (organization.loading) {
    try {
      await organization.isReady();
    } catch {
      // Org loading failed — safest to redirect to login rather than silently
      // allow. Returning true here could bypass access for users with stale
      // pointers to orgs they've been removed from.
      return router.createUrlTree(['/login']);
    }
  }

  const orgId = organization.data?.id;
  const uid = firebase.auth.auth.currentUser?.uid;

  // Can't evaluate access without an (org, user) pair. Route to login rather
  // than granting access.
  if (!orgId || !uid) {
    return router.createUrlTree(['/login']);
  }

  // Ensure the real-time subscription is live before we wait on its output.
  // `subscribeTo` is idempotent (returns early if already subscribed for this
  // (org, user) pair), so this is safe to call on every navigation. Without
  // this, the guard would depend on AppComponent.setupProductAccessGuard
  // having already fired — a timing race that could leave waitForAccessCheck
  // hanging indefinitely on the very first guarded navigation.
  productAccess.subscribeTo(orgId, uid);

  const allowed = await productAccess.isConnectEnabled();
  if (!allowed) {
    return router.createUrlTree(['/product-disabled']);
  }

  return true;
};
